/**
 * RoadmapStationMedia — imágenes (1 o carrusel) + video opcional de una
 * estación, dentro de la tarjeta de detalle de `CoupleRoadmapMap`.
 *
 * Carrusel: scroll-snap horizontal nativo (swipe en mobile gratis) + botones
 * anterior/siguiente y puntos. El índice se sincroniza con el scroll real
 * para que swipe y botones no se desfasen. El padre monta este componente con
 * `key={station.numero}` → al cambiar de estación el carrusel vuelve a la 1.
 *
 * Video (arriba de las imágenes, 2026-09-28b): desplegable cerrado por defecto
 * — fila "▶ Video Dr. Riso ›". Abierto muestra el link directo a YouTube (en
 * el celular abre la app) y, como alternativa para quien no la tiene, el
 * reproductor embebido: fachada con la miniatura; el `<iframe>` (dominio
 * youtube-nocookie) recién se monta al tocar "Ver video" — sin cookies ni
 * requests a YouTube hasta que el paciente lo pide, y sin 1 MB de player
 * cargado de gratis en cada apertura de la tarjeta.
 *
 * Imagen que falla: placeholder numerado (docs/spec_roadmap_pareja_map.md §7).
 *
 * `station.perfiles` (estación 4, 2026-09-28c): en vez del carrusel se monta
 * `RoadmapPerfilSelector` — portada con botones transparentes sobre las
 * tarjetas Konex/Vinka dibujadas en la imagen; cada uno abre su flyer y la
 * flecha arriba-izquierda vuelve a la portada. Info selectiva: la portada no
 * revela el contenido de ninguno de los dos.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties, type FC } from 'react';
import type { RoadmapPerfiles, RoadmapStation, RoadmapZona } from './roadmapSessions';

interface RoadmapStationMediaProps {
  station: RoadmapStation;
  /** Cierra la tarjeta de detalle (flecha dibujada en la portada de perfiles). */
  onClose: () => void;
}

function zonaStyle(zona: RoadmapZona): CSSProperties {
  return {
    left: `${zona.left}%`,
    top: `${zona.top}%`,
    width: `${zona.width}%`,
    height: `${zona.height}%`,
  };
}

interface RoadmapPerfilSelectorProps {
  perfiles: RoadmapPerfiles;
  numero: number;
  titulo: string;
  onClose: () => void;
}

const RoadmapPerfilSelector: FC<RoadmapPerfilSelectorProps> = ({ perfiles, numero, titulo, onClose }) => {
  const [perfilId, setPerfilId] = useState<string | null>(null);
  const [failed, setFailed] = useState<string[]>([]);
  const backRef = useRef<HTMLButtonElement>(null);
  const opcionRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const lastPerfilRef = useRef<string | null>(null);
  const perfil = perfiles.opciones.find((o) => o.id === perfilId) ?? null;

  // Foco al cambiar de vista: a "volver" al abrir un perfil, y de vuelta a la
  // tarjeta que se había abierto al regresar a la portada.
  useEffect(() => {
    if (perfilId) {
      lastPerfilRef.current = perfilId;
      backRef.current?.focus();
    } else if (lastPerfilRef.current) {
      opcionRefs.current[lastPerfilRef.current]?.focus();
    }
  }, [perfilId]);

  const markFailed = (src: string) => setFailed((prev) => (prev.includes(src) ? prev : [...prev, src]));

  const placeholder = (
    <div className="roadmap-map__detail-img roadmap-map__detail-img--placeholder" aria-hidden="true">
      <span>{numero}</span>
    </div>
  );

  if (perfil) {
    return (
      <div className="roadmap-map__perfil">
        {failed.includes(perfil.imagen) ? (
          placeholder
        ) : (
          <a
            className="roadmap-map__detail-img-link"
            href={perfil.imagen}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Ver ${titulo} — ${perfil.etiqueta} en tamaño completo`}
          >
            <img
              className="roadmap-map__detail-img"
              src={perfil.imagen}
              alt={`${titulo} — ${perfil.etiqueta}`}
              width={720}
              height={1280}
              onError={() => markFailed(perfil.imagen)}
            />
          </a>
        )}
        <button
          ref={backRef}
          type="button"
          className="roadmap-map__perfil-back"
          aria-label={`Volver a la estación ${numero}`}
          onClick={() => setPerfilId(null)}
        >
          <svg aria-hidden="true" focusable="false" viewBox="0 0 20 20">
            <path d="M12.5 4.5L7 10l5.5 5.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    );
  }

  if (failed.includes(perfiles.portada)) return placeholder;

  return (
    <div className="roadmap-map__perfil" role="group" aria-label={`${titulo}: elige tu perfil`}>
      <img
        className="roadmap-map__detail-img"
        src={perfiles.portada}
        alt=""
        width={720}
        height={1280}
        onError={() => markFailed(perfiles.portada)}
      />
      <button
        type="button"
        className="roadmap-map__perfil-zona"
        style={zonaStyle(perfiles.zonaVolver)}
        aria-label="Volver al mapa"
        onClick={onClose}
      />
      {perfiles.opciones.map((o) => (
        <button
          key={o.id}
          ref={(el) => {
            opcionRefs.current[o.id] = el;
          }}
          type="button"
          className="roadmap-map__perfil-zona"
          style={zonaStyle(o.zona)}
          aria-label={o.etiqueta}
          onClick={() => setPerfilId(o.id)}
        />
      ))}
    </div>
  );
};

const RoadmapStationMedia: FC<RoadmapStationMediaProps> = ({ station, onClose }) => {
  const { imagenes, titulo, numero, video, perfiles } = station;
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<string[]>([]);
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoOn, setVideoOn] = useState(false);
  const total = imagenes.length;
  const isCarousel = total > 1;

  const goTo = useCallback((i: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.min(Math.max(i, 0), track.children.length - 1);
    track.scrollTo({ left: clamped * track.clientWidth, behavior: 'smooth' });
    setIndex(clamped);
  }, []);

  const handleScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    setIndex(Math.round(track.scrollLeft / track.clientWidth));
  }, []);

  return (
    <div className="roadmap-map__media">
      {video && (
        <div className="roadmap-map__video">
          <button
            type="button"
            className="roadmap-map__video-toggle"
            aria-expanded={videoOpen}
            aria-controls={`roadmap-video-${numero}`}
            onClick={() => setVideoOpen((v) => !v)}
          >
            <svg className="roadmap-map__video-yt" aria-hidden="true" focusable="false" viewBox="0 0 28 20">
              <rect width="28" height="20" rx="5" />
              <path d="M11 5.5v9l7.5-4.5z" />
            </svg>
            <span className="roadmap-map__video-toggle-text">{video.titulo}</span>
            <svg className="roadmap-map__video-chevron" aria-hidden="true" focusable="false" viewBox="0 0 20 20">
              <path d="M7.5 4.5L13 10l-5.5 5.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {videoOpen && (
            <div className="roadmap-map__video-body" id={`roadmap-video-${numero}`}>
              <a
                className="roadmap-map__video-link"
                href={`https://www.youtube.com/watch?v=${video.youtubeId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir en YouTube
              </a>
              <div className="roadmap-map__video-player">
                {videoOn ? (
                  <iframe
                    className="roadmap-map__video-frame"
                    src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0`}
                    title={video.titulo}
                    allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                ) : (
                  <button
                    type="button"
                    className="roadmap-map__video-facade"
                    style={{ backgroundImage: `url('https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg')` }}
                    aria-label={`Ver aquí: ${video.titulo}`}
                    onClick={() => setVideoOn(true)}
                  >
                    <span className="roadmap-map__video-play" aria-hidden="true">
                      <svg viewBox="0 0 24 24" focusable="false">
                        <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
                      </svg>
                    </span>
                    <span className="roadmap-map__video-label">Ver aquí</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {perfiles ? (
        <RoadmapPerfilSelector perfiles={perfiles} numero={numero} titulo={titulo} onClose={onClose} />
      ) : total === 0 ? (
        <div className="roadmap-map__detail-img roadmap-map__detail-img--placeholder" aria-hidden="true">
          <span>{numero}</span>
        </div>
      ) : (
        <div
          className="roadmap-map__carousel"
          role={isCarousel ? 'group' : undefined}
          aria-roledescription={isCarousel ? 'carrusel' : undefined}
          aria-label={isCarousel ? `Imágenes de ${titulo}` : undefined}
        >
          <div className="roadmap-map__carousel-track" ref={trackRef} onScroll={isCarousel ? handleScroll : undefined}>
            {imagenes.map((src, i) => {
              const label = isCarousel ? `${titulo} — imagen ${i + 1} de ${total}` : titulo;
              return (
                <div
                  key={src}
                  className="roadmap-map__carousel-slide"
                  aria-hidden={isCarousel && i !== index ? true : undefined}
                >
                  {failed.includes(src) ? (
                    <div
                      className="roadmap-map__detail-img roadmap-map__detail-img--placeholder"
                      aria-hidden="true"
                    >
                      <span>{numero}</span>
                    </div>
                  ) : (
                    // Flyer vertical 9:16 (720×1280) con texto y QR — completo
                    // (contain) y abre a tamaño real en pestaña nueva.
                    <a
                      className="roadmap-map__detail-img-link"
                      href={src}
                      target="_blank"
                      rel="noopener noreferrer"
                      tabIndex={isCarousel && i !== index ? -1 : undefined}
                      aria-label={`Ver ${label} en tamaño completo`}
                    >
                      <img
                        className="roadmap-map__detail-img"
                        src={src}
                        alt={label}
                        width={720}
                        height={1280}
                        loading="lazy"
                        onError={() => setFailed((prev) => (prev.includes(src) ? prev : [...prev, src]))}
                      />
                    </a>
                  )}
                </div>
              );
            })}
          </div>

          {isCarousel && (
            <div className="roadmap-map__carousel-nav">
              <button
                type="button"
                className="roadmap-map__carousel-btn"
                aria-label="Imagen anterior"
                disabled={index === 0}
                onClick={() => goTo(index - 1)}
              >
                <svg aria-hidden="true" focusable="false" viewBox="0 0 20 20">
                  <path d="M12.5 4.5L7 10l5.5 5.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div className="roadmap-map__carousel-dots" aria-live="polite">
                <span className="roadmap-map__sr-only">
                  Imagen {index + 1} de {total}
                </span>
                {imagenes.map((src, i) => (
                  <span
                    key={src}
                    className="roadmap-map__carousel-dot"
                    data-active={i === index || undefined}
                    aria-hidden="true"
                  />
                ))}
              </div>
              <button
                type="button"
                className="roadmap-map__carousel-btn"
                aria-label="Imagen siguiente"
                disabled={index === total - 1}
                onClick={() => goTo(index + 1)}
              >
                <svg aria-hidden="true" focusable="false" viewBox="0 0 20 20">
                  <path d="M7.5 4.5L13 10l-5.5 5.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default RoadmapStationMedia;
